import type { StateCreator } from 'zustand';
import type {
	BaseCabItemOption,
	BaseContactDetail,
	BaseFarePolicyDetail,
	CabPricing,
	TripDetails,
} from '@/api';
import type { FareDetailSuccessResponse } from '@/api/fareDetails';
import type { CabCoupon } from '@/features/coupon';

/**
 * Review page state interface
 */
export interface ReviewPageState {
	tripDetails: TripDetails | null;
	farePolicies: BaseFarePolicyDetail | null;
	selectedCab: BaseCabItemOption | null;
	contactDetails?: BaseContactDetail;
	/** Flag to track if next loader call should force refresh (e.g., after login invalidation) */
	shouldForceRefresh: boolean;

	coupon?: CabCoupon;
	/**
	 * use this pricing if available or fallback to selectedCab.pricing
	 * gets updated along with coupon
	 */
	pricing?: CabPricing;

	/** user preference to redeem ixigo money during prebook/payment */
	burnOpted?: boolean;
}

/**
 * Review page actions interface
 */
export interface ReviewPageActions {
	// Set fare details from API response
	setFareDetails: (fareDetailsData: FareDetailSuccessResponse['data']) => void;
	// Update contact details when user is updating
	updateContactDetails: (updates: Partial<BaseContactDetail>) => void;
	// Get current review page state
	getReviewStoreState: () => ReviewPageState;
	// Set flag to force refresh on next loader call (e.g., after login)
	setShouldForceRefresh: (shouldForce: boolean) => void;
	setCoupon: (selection: { coupon?: CabCoupon; pricing?: CabPricing }) => void;
	setSelectedCab: (selectedCab: BaseCabItemOption) => void;
	setBurnOpted: (selected: boolean) => void;
}

/**
 * Combined review page slice interface
 */
export interface ReviewPageSlice {
	reviewPage: ReviewPageState;
	reviewPageActions: ReviewPageActions;
}

/**
 * Initial state for review page
 */
const initialState: ReviewPageState = {
	tripDetails: null,
	farePolicies: null,
	contactDetails: undefined,
	shouldForceRefresh: false,
	selectedCab: null,
	burnOpted: true,
};

/**
 * Review page slice creator
 * Manages review page related state and actions with simplified interface
 *
 * Features:
 * - setFareDetails: Set all fare details from API response (trip details, fare summary, policies, contact details)
 * - updateContactDetails: Update existing contact details when user is editing
 *
 * @param set - Zustand setter function
 * @param get - Zustand getter function
 * @param api - Zustand API object
 * @returns ReviewPageSlice object
 */
export const createReviewPageSlice: StateCreator<
	ReviewPageSlice,
	[],
	[],
	ReviewPageSlice
> = (set, get, _api) => ({
	reviewPage: initialState,

	reviewPageActions: {
		// Set fare details from API response
		setFareDetails: (fareDetailsData: FareDetailSuccessResponse['data']) => {
			set((state) => ({
				reviewPage: {
					...state.reviewPage,
					tripDetails: fareDetailsData.tripDetails,
					farePolicies: fareDetailsData.farePolicies,
					contactDetails: fareDetailsData.contactDetails || undefined,
				},
			}));
		},

		getReviewStoreState: () => {
			return get().reviewPage;
		},

		setSelectedCab: (selectedCab: BaseCabItemOption) => {
			set((state) => ({
				reviewPage: {
					...state.reviewPage,
					selectedCab: selectedCab,
				},
			}));
		},

		// Update contact details when user is updating
		updateContactDetails: (updates: Partial<BaseContactDetail>) => {
			set((state) => ({
				reviewPage: {
					...state.reviewPage,
					contactDetails: state.reviewPage.contactDetails
						? { ...state.reviewPage.contactDetails, ...updates }
						: undefined,
				},
			}));
		},
		// Set flag to force refresh on next loader call
		setShouldForceRefresh: (shouldForce: boolean) => {
			set((state) => ({
				reviewPage: {
					...state.reviewPage,
					shouldForceRefresh: shouldForce,
				},
			}));
		},
		setCoupon: (selection) => {
			set((state) => ({
				reviewPage: {
					...state.reviewPage,
					coupon: selection.coupon,
					pricing: selection.pricing,
				},
			}));
		},

		setBurnOpted: (selected: boolean) => {
			set((state) => ({
				reviewPage: {
					...state.reviewPage,
					burnOpted: selected,
				},
			}));
		},
	},
});
